import {
    Component,
    ElementRef,
    OnInit,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { MenuItem, MessageService } from 'primeng/api';
import { LayoutService } from './service/app.layout.service';
import { Router } from '@angular/router';
import { AuthenticationService } from '../auth/authentication.service';
import { Store, select } from '@ngrx/store';
import { selectCurrentUser } from '@app/store/user/user.selectors';
import { UserActions } from '@app/store/user/user.actions';
import { User } from '@app/store/user/user.model';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { MenuService } from './app.menu.service';
import { OverlayPanel } from 'primeng/overlaypanel';
import { NotificationService } from '@app/@shared/notification.service';
import { MessagingService } from '@app/@shared/messaging.service';
import { Menu } from 'primeng/menu';
import { NotificationsPanelComponent } from '@app/@shared/notifications-panel/notifications-panel.component';
import { CeoAuthControllerService, Company } from '@api-swagger-client/index';
import { CredentialsService } from '../auth/credentials.service';
import { PermissionsService } from '@app/@shared/permissions.service';
import { environment } from 'src/environments/environment';

@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    styleUrls: ['./app.topbar.component.scss'],
})
export class AppTopBarComponent implements OnInit, OnDestroy {
    currentUser: User | null = null;
    companyColorStyle: { [klass: string]: any } = {};

    companies: Company[] = [];
    isLoadingCompanies = false;
    showCompanySelector = false;

    unreadCount: number = 0;
    notifications: any[] = [];

    userMenuItems: MenuItem[] = [];

    private destroy$ = new Subject<void>();

    @ViewChild('menubutton') menuButton!: ElementRef;
    @ViewChild('notificationPanel') notificationPanel!: OverlayPanel;
    @ViewChild('menu') menu!: Menu;
    @ViewChild(NotificationsPanelComponent)
    notificationsPanelComponent!: NotificationsPanelComponent;

    constructor(
        public layoutService: LayoutService,
        private router: Router,
        private authenticationService: AuthenticationService,
        private store: Store,
        private menuService: MenuService,
        private notificationService: NotificationService,
        private messagingService: MessagingService,
        private messageService: MessageService,
        private ceoAuthControllerService: CeoAuthControllerService,
        private credentialsService: CredentialsService,
        private permissionsService: PermissionsService
    ) {}

    ngOnInit() {
        this.store.dispatch(UserActions.getCurrentUser());

        this.store
            .pipe(
                select(selectCurrentUser),
                filter((user) => !!user),
                takeUntil(this.destroy$)
            )
            .subscribe((user) => {
                this.currentUser = user;
                this.loadAvailableCompanies();
                this.initializeUserMenu();
            });

        // Mantenemos esto para actualizar el badge del icono
        this.notificationService.unreadCount$
            .pipe(takeUntil(this.destroy$))
            .subscribe((count) => {
                this.unreadCount = count;
            });

        this.messagingService.requestPermission();
        this.messagingService.listen();

        this.messagingService.currentMessage
            .pipe(
                filter((message) => !!message),
                takeUntil(this.destroy$)
            )
            .subscribe((payload) => {
                this.messageService.add({
                    severity: 'info',
                    summary:
                        payload.notification?.title || 'Nueva notificación',
                    detail:
                        payload.notification?.body ||
                        'Tienes una nueva notificación',
                    life: 5000,
                });
                this.notificationService.refreshUnreadCount();
            });
    }

    initializeUserMenu() {
        const items: MenuItem[] = [];

        // availableCompanies solo lo llena el login de CEO; con una sola empresa no hay a cuál cambiar.
        if (this.companies.length > 1) {
            items.push({
                label: 'Cambiar Empresa',
                icon: 'pi pi-building',
                command: () => this.toggleCompanySelector(),
            });
        }

        items.push({
            label: 'Perfil',
            icon: 'pi pi-user',
            command: () => this.viewProfile(),
        });

        // Panel de sesiones activas: UN SOLO ítem. Si el usuario puede administrar
        // sesiones ajenas (ManageAllSessions, por defecto super_admin) ve el panel
        // admin —que ya incluye sus propias sesiones—; en caso contrario ve el de
        // autoservicio (ManageOwnSession, asignado a todos los roles).
        if (this.permissionsService.hasPermission('ManageAllSessions')) {
            items.push({
                label: 'Sesiones activas',
                icon: 'pi pi-desktop',
                command: () => this.viewAdminSessions(),
            });
        } else if (this.permissionsService.hasPermission('ManageOwnSession')) {
            items.push({
                label: 'Sesiones activas',
                icon: 'pi pi-desktop',
                command: () => this.viewActiveSessions(),
            });
        }

        // Fase 3.5 — Administración de CEOs (permiso ManageCeos).
        if (this.permissionsService.hasPermission('ManageCeos')) {
            items.push({
                label: 'Administrar CEOs',
                icon: 'pi pi-briefcase',
                command: () => this.viewCeoAdmins(),
            });
        }

        // Fase 3.5 — Política de seguridad (permiso ManageSecurityPolicies).
        if (this.permissionsService.hasPermission('ManageSecurityPolicies')) {
            items.push({
                label: 'Política de seguridad',
                icon: 'pi pi-shield',
                command: () => this.viewSecurityPolicies(),
            });
        }

        // Versiones de la app y del web (permiso ManageAppVersions).
        if (this.permissionsService.hasPermission('ManageAppVersions')) {
            items.push({
                label: 'Versiones de la app',
                icon: 'pi pi-mobile',
                command: () => this.viewAppReleases(),
            });
        }

        items.push(
            { separator: true },
            {
                label: 'Cerrar Sesión',
                icon: 'pi pi-sign-out',
                command: () => this.logout(),
                styleClass: 'logout-menu-item',
            }
        );

        this.userMenuItems = [{ label: 'Mi Cuenta', items }];
    }

    /**
     * Carga las empresas disponibles para el usuario
     */
    loadAvailableCompanies() {
        try {
            const companiesStr = localStorage.getItem('availableCompanies');
            this.companies = companiesStr ? JSON.parse(companiesStr) : [];

            if (this.companies.length === 0) {
            }
        } catch (error) {
            console.error(
                'Error al cargar empresas desde localStorage:',
                error
            );
            this.companies = [];
        }
    }

    /**
     * Muestra/oculta el selector de empresas
     */
    toggleCompanySelector() {
        this.showCompanySelector = !this.showCompanySelector;
        if (this.showCompanySelector && this.companies.length === 0) {
            this.loadAvailableCompanies();
        }
    }

    /**
     * Cambia a una empresa diferente
     */
    switchCompany(company: Company) {
        if (!company.id) {
            return;
        }

        this.isLoadingCompanies = true;
        const tempToken = localStorage.getItem('tempToken');

        this.ceoAuthControllerService
            .ceoAuthControllerSelectCompany({
                token: tempToken,
                companyId: company.id,
            })
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (tokenResponse) => {
                    this.isLoadingCompanies = false;
                    this.showCompanySelector = false;

                    this.credentialsService.applyTokenResponse(tokenResponse);

                    this.store.dispatch(UserActions.getCurrentUser());

                    this.showToast(
                        'success',
                        `Cambio exitoso`,
                        `Ahora estás en ${company.name}`
                    );

                    setTimeout(() => {
                        window.location.reload();
                    }, 1000);
                },
                error: (error) => {
                    this.isLoadingCompanies = false;
                    this.showToast(
                        'error',
                        'Error al cambiar de empresa',
                        error.error?.message || 'No se pudo completar el cambio'
                    );
                },
            });
    }

    /**
     * Verifica si la empresa actual es la misma que la proporcionada
     */
    isCurrentCompany(company: Company): boolean {
        return this.currentUser?.company?.id === company.id;
    }

    showToast(severity: string, summary: string, detail: string) {
        this.messageService.add({
            key: 'topbar-tst',
            severity: severity,
            summary: summary,
            detail: detail,
        });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    toggleNotifications(event: Event) {
        this.notificationsPanelComponent.toggle(event);
    }

    loadNotifications() {
        this.notificationService.getUnreadNotifications(10).subscribe(
            (notifications: any[]) => {
                this.notifications = notifications;
            },
            (error) => {
                console.error('Error al cargar notificaciones:', error);
                this.notifications = [];
            }
        );
    }

    markAsRead(notification: any) {
        if (!notification.readAt) {
            this.notificationService.markAsRead(notification.id).subscribe(
                () => {
                    notification.readAt = new Date();
                    this.notificationService.refreshUnreadCount();
                },
                (error) => console.error('Error al marcar como leí­da:', error)
            );
        }
    }

    markAllAsRead() {
        this.notificationService.markAllAsRead().subscribe(
            () => {
                this.notifications.forEach((n) => (n.readAt = new Date()));
                this.notificationService.refreshUnreadCount();
            },
            (error) =>
                console.error('Error al marcar todas como leí­das:', error)
        );
    }

    viewAllNotifications() {
        this.notificationPanel.hide();
        this.router.navigate(['/home/notifications']);
    }

    viewProfile() {
        this.router.navigate(['/home/user']);
    }

    viewActiveSessions() {
        this.router.navigate(['/admin/active-sessions']);
    }

    viewAdminSessions() {
        this.router.navigate(['/admin/admin-sessions']);
    }

    viewCeoAdmins() {
        this.router.navigate(['/admin/ceo-admins']);
    }

    viewSecurityPolicies() {
        this.router.navigate(['/admin/security-policies']);
    }

    viewAppReleases() {
        this.router.navigate(['/admin/app-releases']);
    }

    logout() {
        this.menuService.reset();
        this.notificationService.stopPolling();
        this.authenticationService
            .logout()
            .subscribe(() =>
                this.router.navigate(['/auth/login'], { replaceUrl: true })
            );
    }

    getTimeAgo(date: any): string {
        if (!date) return '';

        const now = new Date();
        const notificationDate = new Date(date);
        const diffMs = now.getTime() - notificationDate.getTime();
        const diffMins = Math.floor(diffMs / 60000);
        const diffHours = Math.floor(diffMs / 3600000);
        const diffDays = Math.floor(diffMs / 86400000);

        if (diffMins < 1) return 'Ahora mismo';
        if (diffMins < 60) return `Hace ${diffMins} min`;
        if (diffHours < 24) return `Hace ${diffHours} h`;
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `Hace ${diffDays} dí­as`;

        return notificationDate.toLocaleDateString();
    }

    getCompanyInitial(): string {
        if (this.currentUser?.company?.name) {
            return this.currentUser.company.name.charAt(0).toUpperCase();
        }
        return 'A';
    }

    getUserInitials(): string {
        if (this.currentUser) {
            const firstInitial = this.currentUser.firstName?.charAt(0) || '';
            const lastInitial = this.currentUser.lastName?.charAt(0) || '';
            return (firstInitial + lastInitial).toUpperCase();
        }
        return 'U';
    }
}
